import React, { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Save, Upload, Type, Image as ImageIcon, Move, Palette, 
  Bold, Italic, AlignLeft, AlignCenter, AlignRight, 
  Plus, Minus, Trash2, Eye, Settings, Download
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../services/api';

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 600;
const SCALE_FACTOR = 0.8; // Para visualização reduzida

// Fontes disponíveis
const AVAILABLE_FONTS = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Times-Roman', label: 'Times Roman' },
  { value: 'Courier', label: 'Courier' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Verdana', label: 'Verdana' }
];

const TemplateEditor = ({ template, isCreating, onClose }) => {
  // Estados principais
  const [templateData, setTemplateData] = useState({
    nome: template?.nome || '',
    descricao: template?.descricao || '',
    orientation: template?.orientation || 'horizontal',
    width: template?.width || 800,
    height: template?.height || 600,
    background: template?.background_image || '',
    elements: template?.template_data?.elements || []
  });

  const [selectedElement, setSelectedElement] = useState(null);
  const [dragElement, setDragElement] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  // Elementos de texto padrão disponíveis
  const defaultElements = [
    { type: 'text', content: 'CERTIFICADO', font_size: 48, bold: true, color: '#1e40af', font_family: 'Arial' },
    { type: 'text', content: 'DE CONCLUSÃO', font_size: 24, color: '#64748b', font_family: 'Arial' },
    { type: 'text', content: '{{nome_aluno}}', font_size: 32, bold: true, color: '#0f172a', font_family: 'Arial' },
    { type: 'text', content: '{{nome_curso}}', font_size: 28, bold: true, color: '#1e40af', font_family: 'Arial' },
    { type: 'text', content: 'Data: {{data_conclusao}}', font_size: 16, color: '#64748b', font_family: 'Arial' },
    { type: 'text', content: 'Credencial: {{credential_id}}', font_size: 12, color: '#94a3b8', font_family: 'Arial' }
  ];

  // Adicionar elemento ao canvas
  const addElement = (elementType) => {
    let newElement;
    
    if (elementType === 'text') {
      newElement = {
        id: Date.now(),
        type: 'text',
        content: 'Novo Texto',
        x: 100,
        y: 100,
        font_size: 24,
        font_family: 'Arial',
        color: '#000000',
        align: 'left',
        bold: false,
        italic: false
      };
    } else if (elementType === 'logo') {
      // Trigger logo upload
      logoInputRef.current?.click();
      return;
    }

    setTemplateData(prev => ({
      ...prev,
      elements: [...prev.elements, newElement]
    }));
    setSelectedElement(newElement);
  };

  // Upload de logo
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem');
      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('logo', file);

    try {
      console.log('Enviando logo...', file.name);
      const response = await api.post('/admin/templates/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      console.log('Resposta do upload:', response.data);
      
      // Extrair nome do arquivo da resposta
      let logoSrc = response.data.logo_url;
      
      // Se a URL vier com caminho completo, extrair apenas o nome do arquivo
      if (logoSrc.includes('/uploads/')) {
        logoSrc = logoSrc.split('/uploads/')[1];
      }
      
      const newElement = {
        id: Date.now(),
        type: 'image',
        src: logoSrc,
        x: 100,
        y: 100,
        width: 150,
        height: 150
      };

      setTemplateData(prev => ({
        ...prev,
        elements: [...prev.elements, newElement]
      }));
      setSelectedElement(newElement);
      
      console.log('Logo adicionado:', newElement);
      alert('Logo adicionado com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar logo:', error);
      const errorMsg = error?.response?.data?.erro || error?.message || 'Erro desconhecido';
      alert(`Erro ao enviar logo: ${errorMsg}`);
    }
    
    // Limpar input para permitir upload do mesmo arquivo novamente
    e.target.value = '';
  };

  // Manipular clique no elemento (seleção simples)
  const handleElementClick = (e, element) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedElement(element);
  };

  // Manipular mouse down no elemento (para drag)
  const handleMouseDown = (e, element) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Se já estiver selecionado, inicia o drag
    if (selectedElement?.id === element.id) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      setDragElement(element);
      setDragOffset({
        x: x - (element.x * SCALE_FACTOR),
        y: y - (element.y * SCALE_FACTOR)
      });
    } else {
      // Se não estiver selecionado, apenas seleciona
      setSelectedElement(element);
    }
  };

  // Manipular clique no canvas
  const handleCanvasClick = (e) => {
    e.preventDefault();
    // Só desseleciona se não estiver arrastando
    if (!dragElement) {
      setSelectedElement(null);
    }
  };

  // Manipular movimento do mouse
  const handleMouseMove = useCallback((e) => {
    if (!dragElement || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - dragOffset.x) / SCALE_FACTOR;
    const y = (e.clientY - rect.top - dragOffset.y) / SCALE_FACTOR;

    setTemplateData(prev => ({
      ...prev,
      elements: prev.elements.map(el => 
        el.id === dragElement.id 
          ? { 
              ...el, 
              x: Math.max(0, Math.min(x, prev.width - 50)), 
              y: Math.max(0, Math.min(y, prev.height - 20)) 
            }
          : el
      )
    }));

    // Atualizar elemento selecionado também
    if (selectedElement && selectedElement.id === dragElement.id) {
      setSelectedElement(prev => ({
        ...prev,
        x: Math.max(0, Math.min(x, templateData.width - 50)),
        y: Math.max(0, Math.min(y, templateData.height - 20))
      }));
    }
  }, [dragElement, dragOffset, selectedElement, templateData.width, templateData.height]);

  // Manipular mouse up
  const handleMouseUp = useCallback(() => {
    // Manter o elemento selecionado após o drag
    if (dragElement) {
      setSelectedElement(dragElement);
    }
    setDragElement(null);
  }, [dragElement]);

  // Event listeners para drag & drop
  useEffect(() => {
    if (dragElement) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragElement, handleMouseMove, handleMouseUp]);

  // Atualizar elemento selecionado
  const updateSelectedElement = (updates) => {
    if (!selectedElement) return;

    setTemplateData(prev => ({
      ...prev,
      elements: prev.elements.map(el => 
        el.id === selectedElement.id 
          ? { ...el, ...updates }
          : el
      )
    }));
    
    setSelectedElement(prev => ({ ...prev, ...updates }));
  };

  // Deletar elemento
  const deleteElement = () => {
    if (!selectedElement) return;

    setTemplateData(prev => ({
      ...prev,
      elements: prev.elements.filter(el => el.id !== selectedElement.id)
    }));
    setSelectedElement(null);
  };

  // Upload de background
  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('background', file);

    try {
      const response = await api.post('/admin/templates/upload-background', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setTemplateData(prev => ({
        ...prev,
        background: response.data.background_url
      }));
      
      alert('Background enviado com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar background:', error);
      alert('Erro ao enviar background');
    }
  };

  // Salvar template
  const handleSave = async () => {
    if (!templateData.nome.trim()) {
      alert('Nome do template é obrigatório');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: templateData.nome,
        descricao: templateData.descricao,
        orientation: templateData.orientation,
        width: templateData.width,
        height: templateData.height,
        background_image: templateData.background,
        template_data: {
          background: templateData.background,
          width: templateData.width,
          height: templateData.height,
          elements: templateData.elements
        }
      };

      let templateId;
      if (isCreating) {
        const response = await api.post('/admin/templates', payload);
        templateId = response.data.template_id;
      } else {
        await api.put(`/admin/templates/${template.id}`, payload);
        templateId = template.id;
      }

      // Gerar thumbnail automaticamente após salvar
      if (templateId) {
        try {
          await api.post(`/admin/templates/${templateId}/thumbnail`);
          console.log('Thumbnail gerado com sucesso');
        } catch (thumbnailError) {
          console.warn('Erro ao gerar thumbnail:', thumbnailError);
          // Não bloqueia o salvamento se a thumbnail falhar
        }
      }

      alert('Template salvo com sucesso!');
      onClose();
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      alert('Erro ao salvar template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="flex h-screen">
        {/* Sidebar Esquerda - Ferramentas */}
        <div className="w-80 bg-slate-900/95 border-r border-slate-700 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Header */}
            <div>
              <h2 className="text-xl font-bold text-white mb-1">
                {isCreating ? 'Criar Template' : 'Editar Template'}
              </h2>
              <p className="text-slate-400 text-sm">Configure seu certificado</p>
            </div>

            {/* Informações Básicas */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Nome do Template
                  </label>
                  <input
                    type="text"
                    value={templateData.nome}
                    onChange={(e) => setTemplateData(prev => ({ ...prev, nome: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Certificado Moderno"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={templateData.descricao}
                    onChange={(e) => setTemplateData(prev => ({ ...prev, descricao: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="2"
                    placeholder="Descrição do template"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Orientação
                  </label>
                  <select
                    value={templateData.orientation}
                    onChange={(e) => setTemplateData(prev => ({ ...prev, orientation: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                  >
                    <option value="horizontal">Horizontal</option>
                    <option value="vertical">Vertical</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Largura
                    </label>
                    <input
                      type="number"
                      value={templateData.width}
                      onChange={(e) => setTemplateData(prev => ({ ...prev, width: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Altura
                    </label>
                    <input
                      type="number"
                      value={templateData.height} 
                      onChange={(e) => setTemplateData(prev => ({ ...prev, height: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Background */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <h3 className="text-sm font-medium text-slate-300 mb-3">Imagem de Fundo</h3>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Enviar Imagem
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleBackgroundUpload}
                    className="hidden"
                  />
                  {templateData.background && (
                    <div className="relative">
                      <img
                        src={`http://localhost:5000/uploads/${templateData.background}`}
                        alt="Background"
                        className="w-full h-20 object-cover rounded border border-slate-600"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setTemplateData(prev => ({ ...prev, background: '' }))}
                        className="absolute top-1 right-1 p-1 h-6 w-6 border-red-600/50 text-red-400 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Ferramentas */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <h3 className="text-sm font-medium text-slate-300 mb-3">Adicionar Elementos</h3>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    onClick={() => addElement('text')}
                    className="w-full justify-start border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <Type className="w-4 h-4 mr-2" />
                    Adicionar Texto
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => addElement('logo')}
                    className="w-full justify-start border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Adicionar Logo
                  </Button>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>

                {/* Templates Rápidos */}
                <div className="mt-4">
                  <h4 className="text-xs font-medium text-slate-400 mb-2">Templates Rápidos</h4>
                  <div className="space-y-1">
                    {defaultElements.map((element, index) => (
                      <button
                        key={`default-element-${index}-${element.content.replace(/[^a-zA-Z0-9]/g, '')}`}
                        onClick={() => {
                          const newElement = {
                            ...element,
                            id: Date.now() + index,
                            x: 50 + (index * 20),
                            y: 50 + (index * 40),
                            align: 'center'
                          };
                          setTemplateData(prev => ({
                            ...prev,
                            elements: [...prev.elements, newElement]
                          }));
                        }}
                        className="w-full text-left px-2 py-1 text-xs text-slate-400 hover:text-slate-300 hover:bg-slate-700/50 rounded"
                      >
                        {element.content}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Propriedades do Elemento Selecionado */}
            {selectedElement && selectedElement.type === 'text' && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 space-y-4">
                  <h3 className="text-sm font-medium text-slate-300">Propriedades do Texto</h3>
                  
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">Conteúdo</label>
                    <input
                      type="text"
                      value={selectedElement.content}
                      onChange={(e) => updateSelectedElement({ content: e.target.value })}
                      className="w-full px-2 py-1 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">Fonte</label>
                    <select
                      value={selectedElement.font_family || 'Arial'}
                      onChange={(e) => updateSelectedElement({ font_family: e.target.value })}
                      className="w-full px-2 py-1 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                    >
                      {AVAILABLE_FONTS.map(font => (
                        <option key={font.value} value={font.value}>{font.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Tamanho</label>
                      <input
                        type="number"
                        value={selectedElement.font_size}
                        onChange={(e) => updateSelectedElement({ font_size: parseInt(e.target.value) })}
                        className="w-full px-2 py-1 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Cor</label>
                      <input
                        type="color"
                        value={selectedElement.color}
                        onChange={(e) => updateSelectedElement({ color: e.target.value })}
                        className="w-full h-8 bg-slate-700 border border-slate-600 rounded"
                      />
                    </div>
                  </div>

                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={selectedElement.bold ? "default" : "outline"}
                      onClick={() => updateSelectedElement({ bold: !selectedElement.bold })}
                      className="flex-1"
                    >
                      <Bold className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedElement.italic ? "default" : "outline"}
                      onClick={() => updateSelectedElement({ italic: !selectedElement.italic })}
                      className="flex-1"
                    >
                      <Italic className="w-3 h-3" />
                    </Button>
                  </div>

                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={selectedElement.align === 'left' ? "default" : "outline"}
                      onClick={() => updateSelectedElement({ align: 'left' })}
                      className="flex-1"
                    >
                      <AlignLeft className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedElement.align === 'center' ? "default" : "outline"}
                      onClick={() => updateSelectedElement({ align: 'center' })}
                      className="flex-1"
                    >
                      <AlignCenter className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedElement.align === 'right' ? "default" : "outline"}
                      onClick={() => updateSelectedElement({ align: 'right' })}
                      className="flex-1"
                    >
                      <AlignRight className="w-3 h-3" />
                    </Button>
                  </div>

                  <div className="text-xs text-slate-400 bg-slate-900/50 p-2 rounded">
                    <strong>Dica:</strong> Clique no elemento para selecioná-lo, clique novamente e arraste para mover.
                  </div>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={deleteElement}
                    className="w-full border-red-600/50 text-red-400 hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Deletar Elemento
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Propriedades do Logo Selecionado */}
            {selectedElement && selectedElement.type === 'image' && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 space-y-4">
                  <h3 className="text-sm font-medium text-slate-300">Propriedades da Imagem</h3>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Posição X</label>
                      <input
                        type="number"
                        value={Math.round(selectedElement.x)}
                        onChange={(e) => updateSelectedElement({ x: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Posição Y</label>
                      <input
                        type="number"
                        value={Math.round(selectedElement.y)}
                        onChange={(e) => updateSelectedElement({ y: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Largura</label>
                      <input
                        type="number"
                        value={selectedElement.width}
                        onChange={(e) => updateSelectedElement({ width: parseInt(e.target.value) || 50 })}
                        className="w-full px-2 py-1 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                        min="10"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Altura</label>
                      <input
                        type="number"
                        value={selectedElement.height}
                        onChange={(e) => updateSelectedElement({ height: parseInt(e.target.value) || 50 })}
                        className="w-full px-2 py-1 text-sm bg-slate-700 border border-slate-600 rounded text-white"
                        min="10"
                      />
                    </div>
                  </div>

                  <div className="text-xs text-slate-400 bg-slate-900/50 p-2 rounded">
                    <strong>Dica:</strong> Clique no elemento para selecioná-lo, clique novamente e arraste para mover.
                  </div>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={deleteElement}
                    className="w-full border-red-600/50 text-red-400 hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Deletar Elemento
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Área Principal - Canvas */}
        <div className="flex-1 flex flex-col">
          {/* Toolbar Superior */}
          <div className="bg-slate-900/95 border-b border-slate-700 p-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="border-slate-600 text-slate-300">
                {templateData.width} × {templateData.height}px
              </Badge>
              <Badge variant="outline" className="border-slate-600 text-slate-300">
                {templateData.elements.length} elemento(s)
              </Badge>
              {selectedElement && (
                <Badge variant="outline" className="border-blue-600 text-blue-300">
                  {selectedElement.type === 'text' ? 'Texto' : 'Imagem'} selecionado
                  {dragElement && dragElement.id === selectedElement.id && (
                    <span className="ml-2 text-yellow-400">(Movendo...)</span>
                  )}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Eye className="w-4 h-4 mr-2" />
                {isPreviewMode ? 'Editar' : 'Preview'}
              </Button>
              
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>

              <Button
                variant="outline"
                onClick={onClose}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Fechar
              </Button>
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 p-8 bg-slate-800/30 overflow-auto">
            <div className="flex justify-center">
              <div
                ref={canvasRef}
                className="relative bg-slate-50 shadow-2xl border border-slate-600 cursor-default"
                style={{
                  width: templateData.width * SCALE_FACTOR,
                  height: templateData.height * SCALE_FACTOR,
                  backgroundImage: templateData.background 
                    ? `url(http://localhost:5000/uploads/${templateData.background})`
                    : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
                onClick={handleCanvasClick}
              >
                {/* Renderizar elementos */}
                {templateData.elements.map((element) => (
                  <div
                    key={element.id}
                    className={`absolute select-none transition-all duration-200 ${
                      selectedElement?.id === element.id 
                        ? 'ring-2 ring-blue-500 ring-opacity-75 shadow-lg cursor-move' 
                        : 'hover:ring-1 hover:ring-blue-300 cursor-pointer'
                    }`}
                    style={{
                      left: element.x * SCALE_FACTOR,
                      top: element.y * SCALE_FACTOR,
                      fontSize: element.type === 'text' ? (element.font_size || 16) * SCALE_FACTOR : undefined,
                      color: element.color || '#000000',
                      fontFamily: element.font_family || 'Arial',
                      fontWeight: element.bold ? 'bold' : 'normal',
                      fontStyle: element.italic ? 'italic' : 'normal',
                      textAlign: element.align || 'left',
                      transform: element.align === 'center' ? 'translateX(-50%)' : 'none',
                      width: element.type === 'image' ? element.width * SCALE_FACTOR : 'auto',
                      height: element.type === 'image' ? element.height * SCALE_FACTOR : 'auto',
                      zIndex: selectedElement?.id === element.id ? 10 : 1
                    }}
                    onClick={(e) => handleElementClick(e, element)}
                    onMouseDown={(e) => handleMouseDown(e, element)}
                  >
                    {element.type === 'text' && element.content}
                    {element.type === 'image' && (
                      <img
                        src={`http://localhost:5000/uploads/${element.src}`}
                        alt="Logo"
                        className="w-full h-full object-contain pointer-events-none"
                        style={{
                          width: element.width * SCALE_FACTOR,
                          height: element.height * SCALE_FACTOR
                        }}
                        onError={(e) => {
                          console.error('Erro ao carregar imagem:', element.src);
                          e.target.style.display = 'none';
                          // Mostrar placeholder
                          e.target.parentElement.innerHTML = `
                            <div class="flex items-center justify-center bg-gray-200 text-gray-500 text-xs">
                              Erro ao carregar imagem
                            </div>
                          `;
                        }}
                        onLoad={() => {
                          console.log('Imagem carregada com sucesso:', element.src);
                        }}
                      />
                    )}
                  </div>
                ))}

                {/* Grid de Ajuda */}
                {!isPreviewMode && (
                  <div className="absolute inset-0 pointer-events-none opacity-10">
                    {/* Linhas verticais */}
                    {[...Array(Math.floor(templateData.width / 50))].map((_, i) => (
                      <div
                        key={`vertical-grid-line-${i}-${templateData.width}`}
                        className="absolute top-0 bottom-0 w-px bg-slate-400"
                        style={{ left: (i * 50 + 50) * SCALE_FACTOR }}
                      />
                    ))}
                    {/* Linhas horizontais */}
                    {[...Array(Math.floor(templateData.height / 50))].map((_, i) => (
                      <div
                        key={`horizontal-grid-line-${i}-${templateData.height}`}
                        className="absolute left-0 right-0 h-px bg-slate-400"
                        style={{ top: (i * 50 + 50) * SCALE_FACTOR }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor; 